import React, { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { apiService } from '../services/api';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  padding: 1.5rem;
`;

const Title = styled.h1`
  margin: 0 0 0.75rem 0;
  font-size: 1.4rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: 2px solid rgba(102,126,234,0.2);
  margin-bottom: 0.75rem;
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: none;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #11998e, #38ef7d);
  cursor: pointer;
`;

const Msg = styled.div<{ type: 'error'|'success' }>`
  margin-bottom: 0.75rem;
  padding: 0.6rem 0.8rem;
  border-radius: 10px;
  color: #fff;
  background: ${p => p.type === 'success' ? 'linear-gradient(135deg,#11998e,#38ef7d)' : 'linear-gradient(135deg,#fc4a1a,#f7b733)'};
`;

const RuleList = styled.ul`
  list-style: none;
  margin: 0 0 0.5rem 0;
  padding: 0;
  font-size: 0.85rem;
  color: #374151;
  li { display: flex; align-items: center; gap: 0.4rem; margin: 0.15rem 0; }
`;

const StrengthBar = styled.div<{ level: 'weak'|'medium'|'strong' }>`
  height: 8px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
  margin: 0.25rem 0 0.5rem 0;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: ${p => p.level === 'weak' ? '33%' : p.level === 'medium' ? '66%' : '100%'};
    background: ${p => p.level === 'weak' ? '#ef4444' : p.level === 'medium' ? '#f59e0b' : '#10b981'};
  }
`;

const StrengthLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: #374151;
`;

const SetPassword: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [msg, setMsg] = useState<string|null>(null);
  const [err, setErr] = useState<string|null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null); setErr(null);
    if (!token) { setErr('Invalid or missing token'); return; }
    // Client-side policy: min 9, at least one uppercase, one lowercase, one number, and one special char
    if (p1.length < 9) { setErr('Password must be at least 9 characters'); return; }
    const hasUpper = /[A-Z]/.test(p1);
    const hasLower = /[a-z]/.test(p1);
    const hasDigit = /[0-9]/.test(p1);
    const hasSpecial = /[^A-Za-z0-9]/.test(p1);
    if (!(hasUpper && hasLower && hasDigit && hasSpecial)) {
      setErr('Password must include uppercase, lowercase, number, and special character');
      return;
    }
    if (p1 !== p2) { setErr('Passwords do not match'); return; }
    try {
      setSaving(true);
      const resp = await apiService.post<any>('/api/auth/set-password', { token, newPassword: p1 });
      setMsg(resp?.message || 'Password set successfully');
      setTimeout(() => navigate('/portal/login'), 1500);
    } catch (e: any) {
      setErr(e?.message || 'Failed to set password');
    } finally { setSaving(false); }
  };

  return (
    <Wrapper>
      <Card>
        <Title>Set your password</Title>
        {msg && <Msg type="success">✅ {msg}</Msg>}
        {err && <Msg type="error">⚠️ {err}</Msg>}
        <form onSubmit={onSubmit}>
          <Input type="password" placeholder="New password" value={p1} onChange={e => setP1(e.target.value)} />
          {/* Password strength and rules */}
          <StrengthLabel>Password strength</StrengthLabel>
          <StrengthBar level={(function(){
            const len = p1.length;
            const hasUpper = /[A-Z]/.test(p1);
            const hasLower = /[a-z]/.test(p1);
            const hasDigit = /[0-9]/.test(p1);
            const hasSpecial = /[^A-Za-z0-9]/.test(p1);
            const score = (len >= 9 ? 1 : 0) + (hasUpper?1:0)+(hasLower?1:0)+(hasDigit?1:0)+(hasSpecial?1:0);
            if (score >= 5 && len >= 12) return 'strong';
            if (score >= 4) return 'medium';
            return 'weak';
          })() as 'weak'|'medium'|'strong'} />
          <RuleList>
            <li>{p1.length >= 9 ? '✅' : '⚠️'} At least 9 characters</li>
            <li>{/[A-Z]/.test(p1) ? '✅' : '⚠️'} Contains an uppercase letter (A–Z)</li>
            <li>{/[a-z]/.test(p1) ? '✅' : '⚠️'} Contains a lowercase letter (a–z)</li>
            <li>{/[0-9]/.test(p1) ? '✅' : '⚠️'} Contains a number (0–9)</li>
            <li>{/[^A-Za-z0-9]/.test(p1) ? '✅' : '⚠️'} Contains a special character (!@#$…)</li>
          </RuleList>
          <Input type="password" placeholder="Confirm password" value={p2} onChange={e => setP2(e.target.value)} />
          <Button disabled={saving}>{saving ? 'Saving…' : 'Set Password'}</Button>
        </form>
      </Card>
    </Wrapper>
  );
};

export default SetPassword;
